import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabStrip } from '../components/TabStrip.js';
import { describeError } from '../errors.js';
import type { AdminHttpClient } from '../http.js';
import type { Partner } from '../api.js';
import type {
  ProviderKeySummary,
  PlatformProviderModelDefinition,
  Tenant,
  TenantAssignment,
  TenantMediaRouteSummary,
  TenantWebSearchBackendSummary,
  ToastMessage,
  DesktopModelView,
} from '../types.js';
import type { TenantPartnerTransferResult } from '../../../shared-types/src/index.js';
import {
  ModelAssignmentTab,
  assignmentSelectionFrom,
  emptyAssignmentSelection,
  normalizeAssignmentSelection,
  type ModelAssignmentSelection,
} from './platform/ModelAssignmentTab.js';
import { AutoRoutingTab } from './platform/AutoRoutingTab.js';
import { RecommendedModelsPanel } from './platform/RecommendedModelsPanel.js';
import { TenantLoginDefaultModelScreen } from './TenantLoginDefaultModel.js';
import { ModelsScreen } from './Models.js';
import { CustomModelDefinitionsPanel } from './platform/CustomModelDefinitionsPanel.js';
import { CustomModelDeploymentSection } from './platform/CustomModelDeploymentSection.js';
import { GeminiSearchSettings } from './platform/GeminiSearchSettings.js';
import { ManualProviderModelsPanel } from './platform/ManualProviderModelsPanel.js';
import { ModelDeploymentPanel } from './platform/ModelDeploymentPanel.js';
import { MediaGenerationSettings } from './platform/MediaGenerationSettings.js';
import { ProviderKeysTab } from './platform/ProviderKeysTab.js';
import { TenantsTab } from './platform/TenantsTab.js';
import { ToolActivationTab } from './platform/ToolActivationTab.js';
import { CapabilitySourcesTab } from './platform/CapabilitySourcesTab.js';
import { SocialLoginTab } from './platform/SocialLoginTab.js';
import { OrganizationSsoTab } from './platform/OrganizationSsoTab.js';
import { SignupTab } from './platform/SignupTab.js';
import { VoiceSettings } from './platform/VoiceFeatureSettingsPanel.js';
import { PROVIDERS } from './platform/base-urls.js';
import { modelSettingsOnboardingProgress } from './platform/onboarding-progress.js';

const MODEL_SETTINGS_GROUPS = [
  { label: 'platform.settingsGroupPlatform', items: [
    { id: 'manualProviderModels', label: 'Model definitions' },
    { id: 'capabilitySources', label: 'Capability sources' },
  ] },
  { label: 'platform.settingsGroupConnection', items: [
    { id: 'keys', label: 'Provider keys' },
    { id: 'modelDeployment', label: 'Deployment & rates' },
  ] },
  { label: 'platform.settingsGroupDefaults', items: [
    { id: 'assignment', label: 'Model availability settings' },
    { id: 'autoRouting', label: 'Auto routing' },
    { id: 'recommendedModels', label: 'Recommended models' },
    { id: 'loginDefault', label: 'Login default model' },
    { id: 'modelAccess', label: 'User and group assignment' },
    { id: 'tools', label: 'Tool activation' },
  ] },
  { label: 'platform.settingsGroupOptional', items: [
    { id: 'imageGeneration', label: 'Image generation' },
    { id: 'videoGeneration', label: 'Video generation' },
    { id: 'voice', label: 'Voice' },
    { id: 'geminiSearch', label: 'Platform Gemini search key' },
  ] },
] as const;
const ACCESS_SETTINGS_TABS = [
  { id: 'socialLogin', label: 'platform.socialLogin' },
  { id: 'selfSignup', label: 'platform.selfSignup' },
  { id: 'organizationSso', label: 'platform.organizationSso' },
] as const;
type ModelSettingsTabId = (typeof MODEL_SETTINGS_GROUPS)[number]['items'][number]['id'];
type AccessSettingsTabId = (typeof ACCESS_SETTINGS_TABS)[number]['id'];
type DestinationTabId = ModelSettingsTabId | AccessSettingsTabId;
type TabId = 'tenants' | DestinationTabId;
type TabScope = 'tenants' | 'modelSettings' | 'accessSettings';
type DefinitionKind = 'provider' | 'dedicated';
const PLATFORM_GLOBAL_DESTINATIONS = new Set<ModelSettingsTabId>([
  'manualProviderModels',
  'capabilitySources',
]);
const MODEL_SETTINGS_DESTINATIONS = new Set<DestinationTabId>(
  MODEL_SETTINGS_GROUPS.flatMap((group) => group.items.map((item) => item.id))
);
const ACCESS_SETTINGS_DESTINATIONS = new Set<DestinationTabId>(
  ACCESS_SETTINGS_TABS.map((item) => item.id)
);

function MicIcon() {
  return (
    <svg className="nav-icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="5.5" y="1.5" width="5" height="8" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 7.5a5 5 0 0 0 10 0M8 12.5v2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function isModelSettingsTab(tab: TabId): tab is ModelSettingsTabId {
  return tab !== 'tenants' && MODEL_SETTINGS_DESTINATIONS.has(tab);
}

function isAccessSettingsTab(tab: TabId): tab is AccessSettingsTabId {
  return tab !== 'tenants' && ACCESS_SETTINGS_DESTINATIONS.has(tab);
}

const TAB_I18N: Record<TabId, string> = {
  tenants: 'platform.tenants',
  keys: 'platform.providerKeys',
  geminiSearch: 'platform.platformGeminiSearchKey',
  imageGeneration: 'platform.imageGeneration',
  videoGeneration: 'platform.videoGeneration',
  voice: 'voiceAdmin.menu',
  manualProviderModels: 'platform.manualProviderModels',
  modelDeployment: 'platform.modelDeployment',
  assignment: 'platform.modelAssignment',
  autoRouting: 'platform.autoRouting',
  recommendedModels: 'recommendedModels.title',
  loginDefault: 'recommendedModels.links.loginDefault',
  modelAccess: 'recommendedModels.links.modelAccess',
  tools: 'platform.toolActivation',
  capabilitySources: 'platform.capabilitySources',
  socialLogin: 'platform.socialLogin',
  selfSignup: 'platform.selfSignup',
  organizationSso: 'platform.organizationSso',
};

export type PlatformAssignmentSaveContext = Readonly<{
  tenantId: string;
  generation: number;
}>;

export async function runPlatformAssignmentSave({
  context,
  selection,
  request,
  isCurrent,
  onApplied,
  onError,
  setBusy,
}: {
  context: PlatformAssignmentSaveContext;
  selection: ModelAssignmentSelection;
  request: (tenantId: string, selection: ModelAssignmentSelection) => Promise<TenantAssignment>;
  isCurrent: (context: PlatformAssignmentSaveContext) => boolean;
  onApplied: (assignment: TenantAssignment) => void;
  onError: (error: unknown) => void;
  setBusy: (busy: boolean) => void;
}): Promise<'applied' | 'stale' | 'error'> {
  setBusy(true);
  try {
    const next = await request(context.tenantId, selection);
    if (!isCurrent(context)) return 'stale';
    onApplied(next);
    return 'applied';
  } catch (error) {
    if (!isCurrent(context)) return 'stale';
    onError(error);
    return 'error';
  } finally {
    if (isCurrent(context)) setBusy(false);
  }
}

export async function runPlatformTenantReload<T>({
  targetTenantId,
  getSelectedTenantId,
  begin,
  isGenerationCurrent,
  load,
  apply,
}: {
  targetTenantId: string;
  getSelectedTenantId: () => string;
  begin: () => number;
  isGenerationCurrent: (generation: number) => boolean;
  load: () => Promise<T>;
  apply: (result: T) => void;
}): Promise<'applied' | 'stale'> {
  if (targetTenantId !== getSelectedTenantId()) return 'stale';
  const generation = begin();
  try {
    const result = await load();
    if (targetTenantId !== getSelectedTenantId() || !isGenerationCurrent(generation))
      return 'stale';
    apply(result);
    return 'applied';
  } catch (error) {
    if (targetTenantId !== getSelectedTenantId() || !isGenerationCurrent(generation))
      return 'stale';
    throw error;
  }
}

/** Platform console container: owns the active tenant + the data shared
 *  across model-settings destinations (tenant list, provider keys, model assignment) and the
 *  two-way sync with the topbar tenant context. The tenant selector stays
 *  above the workspace so every task is unambiguous about which tenant it
 *  mutates. */
export function PlatformScreen({
  http,
  tenantHttp,
  onToast,
  selectedTenantId,
  onTenantContext,
  tabScope,
}: {
  http: AdminHttpClient;
  tenantHttp?: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  /** Tenant context selected in the top bar. */
  selectedTenantId?: string;
  onTenantContext: (tenantId: string) => Promise<boolean>;
  tabScope?: TabScope;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const tenantId = selectedTenantId ?? '';
  const [keys, setKeys] = useState<ProviderKeySummary[]>([]);
  const [webSearchBackends, setWebSearchBackends] = useState<TenantWebSearchBackendSummary[]>([]);
  const [mediaRoutes, setMediaRoutes] = useState<TenantMediaRouteSummary[]>([]);
  const [assignment, setAssignment] = useState<TenantAssignment>({
    tenantId: '',
    allowedModelIds: [],
    defaultMemberModelIds: [],
    updatedAt: new Date().toISOString(),
  });
  const [catalog, setCatalog] = useState<DesktopModelView[]>([]);
  const [providerModels, setProviderModels] = useState<PlatformProviderModelDefinition[]>([]);
  const [selectedProviderModel, setSelectedProviderModel] =
    useState<PlatformProviderModelDefinition>();
  const [definitionKind, setDefinitionKind] = useState<DefinitionKind>('provider');
  const [customModelPartnerId, setCustomModelPartnerId] = useState('');
  const [assignmentSelection, setAssignmentSelection] =
    useState<ModelAssignmentSelection>(emptyAssignmentSelection);
  const { selected, selectedDefaults, selectedFreeTier } = assignmentSelection;
  const [assignableConnectors, setAssignableConnectors] = useState<string[]>([]);
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const reloadRequestRef = useRef(0);
  const loadedTenantIdRef = useRef('');
  const selectedTenantIdRef = useRef(tenantId);
  selectedTenantIdRef.current = tenantId;
  const assignmentContextRef = useRef<PlatformAssignmentSaveContext>({
    tenantId,
    generation: 0,
  });
  if (assignmentContextRef.current.tenantId !== tenantId) {
    assignmentContextRef.current = {
      tenantId,
      generation: assignmentContextRef.current.generation + 1,
    };
  }
  const lastModelSettingsTab = useRef<ModelSettingsTabId>('keys');
  const lastAccessSettingsTab = useRef<AccessSettingsTabId>('socialLogin');

  const loadTenants = useCallback(async (): Promise<Tenant[]> => {
    const next = await http.getTenants();
    setTenants(next);
    return next;
  }, [http]);

  const loadPartners = useCallback(async (): Promise<Partner[]> => {
    const next = await http.listPartners();
    setPartners(next);
    return next;
  }, [http]);

  const reload = useCallback(
    async (targetTenantId: string) => {
      await runPlatformTenantReload({
        targetTenantId,
        getSelectedTenantId: () => selectedTenantIdRef.current,
        begin: () => {
          const requestGeneration = ++reloadRequestRef.current;
          assignmentContextRef.current = {
            tenantId: selectedTenantIdRef.current,
            generation: assignmentContextRef.current.generation + 1,
          };
          setAssignmentBusy(false);
          if (loadedTenantIdRef.current !== selectedTenantIdRef.current) {
            loadedTenantIdRef.current = selectedTenantIdRef.current;
            setKeys([]);
            setWebSearchBackends([]);
            setMediaRoutes([]);
            setAssignment({
              tenantId: '',
              allowedModelIds: [],
              defaultMemberModelIds: [],
              updatedAt: new Date().toISOString(),
            });
            setCatalog([]);
            setAssignmentSelection(emptyAssignmentSelection());
            setAssignableConnectors([]);
            setSelectedConnectors([]);
          }
          return requestGeneration;
        },
        isGenerationCurrent: (generation) => generation === reloadRequestRef.current,
        load: () =>
          targetTenantId
            ? Promise.all([
                http.getProviderKeys(targetTenantId),
                http.listWebSearchBackends(targetTenantId),
                http.listMediaRoutes(targetTenantId),
                http.getTenantAssignment(targetTenantId),
                http.getTenantMcpConnectors(targetTenantId),
                http.getTenantCatalog(targetTenantId),
              ])
            : Promise.resolve(undefined),
        apply: (result) => {
          if (!result) return;
          const [
            nextKeys,
            nextWebSearchBackends,
            nextMediaRoutes,
            nextAssignment,
            nextConnectors,
            nextCatalog,
          ] = result;
          setKeys(nextKeys);
          setWebSearchBackends(nextWebSearchBackends);
          setMediaRoutes(nextMediaRoutes);
          setAssignment(nextAssignment);
          setCatalog(nextCatalog);
          setAssignmentSelection(assignmentSelectionFrom(nextAssignment));
          setAssignableConnectors(nextConnectors.assignableMcpConnectors);
          setSelectedConnectors(nextConnectors.activeMcpConnectors);
        },
      });
    },
    [http]
  );

  // Load platform lists whenever the adapter (and thus the session token) changes.
  useEffect(() => {
    void Promise.allSettled([
      loadTenants().catch((error) => {
        onToast({ tone: 'error', message: describeError(error, t) });
        return [];
      }),
      loadPartners().catch((error) => {
        setPartners([]);
        onToast({ tone: 'error', message: describeError(error, t) });
        return [];
      }),
    ]);
  }, [loadPartners, loadTenants, onToast, t]);

  // The top-bar context is the single source of truth. Every change starts one
  // generation-protected reload; stale tenant responses are ignored.
  useEffect(() => {
    void reload(tenantId).catch((error) =>
      onToast({ tone: 'error', message: describeError(error, t) })
    );
  }, [tenantId, reload, onToast, t]);

  async function createTenant(input: { name: string; partnerId?: string }): Promise<boolean> {
    setBusy(true);
    try {
      const { name, partnerId } = input;
      const tenant = await http.createTenant({
        name,
        ...(partnerId ? { partnerId } : {}),
      });
      await loadTenants();
      const contextSelected = await onTenantContext(tenant.id);
      onToast({
        tone: contextSelected ? 'success' : 'info',
        message: contextSelected
          ? partnerId
            ? t('platform.tenantCreatedUnderPartner', {
                name: tenant.name,
                id: tenant.id,
                partner: partners.find((partner) => partner.id === partnerId)?.name ?? partnerId,
              })
            : t('platform.tenantCreated', { name: tenant.name, id: tenant.id })
          : t('platform.tenantCreatedContextFailed', { name: tenant.name, id: tenant.id }),
      });
      return true;
    } catch (error) {
      // The tenant-naming codes (tenant_name_invalid, …) localize through the
      // errors.codes.* table just like every other business code now.
      onToast({ tone: 'error', message: describeError(error, t) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function deleteTenant(tenant: Tenant): Promise<void> {
    const confirmName =
      window.prompt(t('platform.confirmDeleteTenant', { name: tenant.name })) ?? '';
    if (!confirmName) return;
    setBusy(true);
    try {
      await http.deleteTenant(tenant.id, confirmName);
      await loadTenants();
      if (tenantId === tenant.id) {
        await onTenantContext('');
      }
      onToast({ tone: 'success', message: t('platform.tenantDeleted') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  }

  async function previewTenantPartnerTransfer(
    tenant: Tenant,
    partnerId: string | null
  ): Promise<TenantPartnerTransferResult | undefined> {
    setBusy(true);
    try {
      return await http.transferTenantPartner(tenant.id, { partnerId, dryRun: true });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  async function transferTenantPartner(tenant: Tenant, partnerId: string | null): Promise<boolean> {
    const confirmName =
      window.prompt(t('platform.confirmTransferTenant', { name: tenant.name })) ?? '';
    if (!confirmName) return false;
    setBusy(true);
    try {
      await http.transferTenantPartner(tenant.id, { partnerId, confirmName });
      await loadTenants();
      if (tenantId === tenant.id) await reload(tenant.id);
      onToast({ tone: 'success', message: t('platform.tenantTransferred') });
      return true;
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function saveAssignment() {
    const context = assignmentContextRef.current;
    if (!context.tenantId.trim()) return;
    const nextSelection = normalizeAssignmentSelection({
      selected,
      selectedDefaults,
      selectedFreeTier,
    });
    await runPlatformAssignmentSave({
      context,
      selection: nextSelection,
      request: (targetTenantId, targetSelection) =>
        http.setTenantAssignment(
          targetTenantId.trim(),
          targetSelection.selected,
          targetSelection.selectedDefaults,
          targetSelection.selectedFreeTier
        ),
      isCurrent: (candidate) => assignmentContextRef.current === candidate,
      onApplied: (next) => {
        setAssignment(next);
        setAssignmentSelection(assignmentSelectionFrom(next));
        onToast({ tone: 'success', message: t('common.changed') });
      },
      onError: (error) => {
        onToast({ tone: 'error', message: describeError(error, t) });
      },
      setBusy: setAssignmentBusy,
    });
  }

  async function saveConnectors() {
    if (!tenantId.trim()) return;
    setBusy(true);
    try {
      // Empty selection is intentional (revokes every connector), so we always
      // send `selectedConnectors` as-is — no zero-length guard.
      const next = await http.setTenantMcpConnectors(tenantId.trim(), selectedConnectors);
      setSelectedConnectors(next.activeMcpConnectors ?? []);
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (isModelSettingsTab(tab)) lastModelSettingsTab.current = tab;
    if (isAccessSettingsTab(tab)) lastAccessSettingsTab.current = tab;
    if (tabScope === 'tenants' && tab !== 'tenants') setTab('tenants');
    if (tabScope === 'modelSettings' && !isModelSettingsTab(tab))
      setTab(lastModelSettingsTab.current);
    if (tabScope === 'accessSettings' && !isAccessSettingsTab(tab))
      setTab(lastAccessSettingsTab.current);
  }, [tab, tabScope]);

  const onboardingProgress = modelSettingsOnboardingProgress(
    keys,
    PROVIDERS.length,
    catalog,
    assignment.allowedModelIds
  );
  const activeTenant = tenants.find((tenant) => tenant.id === tenantId);
  const isPlatformGlobalDestination =
    isModelSettingsTab(tab) && PLATFORM_GLOBAL_DESTINATIONS.has(tab);
  const showTenantScope =
    isAccessSettingsTab(tab) || (isModelSettingsTab(tab) && !isPlatformGlobalDestination);

  const activeModelGroup = MODEL_SETTINGS_GROUPS.find((group) => group.items.some((item) => item.id === tab));
  return (
    <section className="content-stack">
      {!isModelSettingsTab(tab) ? <p className="screen-help">{t('screenHelp.platform')}</p> : null}
      {showTenantScope && !tenantId ? <p className="platform-scope-summary">{t('platform.scopeNoTenant')}</p> : null}
      {tab === 'voice' ? <p className="platform-scope-summary">{t('voiceFeature.mixedScope')}</p> : null}
      {/* Destinations stay mounted (hidden, not unmounted) so in-progress form state —
          a pasted API key, an edited base URL, a typed tenant name — survives
          switching tabs to check something. */}
      <div hidden={tab !== 'tenants'}>
        <TenantsTab
          busy={busy}
          tenants={tenants}
          partners={partners}
          selectedTenantId={tenantId}
          onCreate={createTenant}
          onSelect={onTenantContext}
          onDelete={deleteTenant}
          onTransferPreview={previewTenantPartnerTransfer}
          onTransfer={transferTenantPartner}
        />
      </div>
      <div className="platform-settings-workspace" hidden={tab === 'tenants'}>
        {isModelSettingsTab(tab) ? (
          <>
            <label className="compact-navigation">
              {t('platform.settingsNavigation')}
              <select value={tab} onChange={(event) => setTab(event.target.value as ModelSettingsTabId)}>
                {MODEL_SETTINGS_GROUPS.map((group) => (
                  <optgroup key={group.label} label={t(group.label)}>
                    {group.items.map((item) => <option key={item.id} value={item.id}>{t(TAB_I18N[item.id], item.label)}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <nav className="platform-settings-nav wide-navigation" aria-label={t('platform.settingsNavigation')}>
              <div className="platform-settings-groups" role="group" aria-label={t('platform.settingsGroups')}>
                {MODEL_SETTINGS_GROUPS.map((group) => (
                  <button key={group.label} type="button" className="secondary"
                    aria-pressed={activeModelGroup === group}
                    onClick={() => setTab(group.items[0].id)}>{t(group.label)}</button>
                ))}
              </div>
              <div className="platform-settings-nav-items">
                {activeModelGroup?.items.map((item) => (
                  <button key={item.id} type="button"
                    className={tab === item.id ? 'platform-settings-nav-item active' : 'platform-settings-nav-item'}
                    aria-current={tab === item.id ? 'page' : undefined}
                    onClick={() => setTab(item.id)}>
                    {item.id === 'voice' ? <MicIcon /> : null}{t(TAB_I18N[item.id], item.label)}
                  </button>
                ))}
              </div>
            </nav>
            <h2 className="model-destination-title">{t(TAB_I18N[tab])}</h2>

          </>
        ) : null}
        <TabStrip
          tabs={ACCESS_SETTINGS_TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
          value={isAccessSettingsTab(tab) ? tab : lastAccessSettingsTab.current}
          onChange={setTab}
          hidden={!isAccessSettingsTab(tab)}
          ariaLabel={t('platformConsole.tabAccessSettings')}
          level="nested"
        />
        <div className="platform-settings-content">
          {isPlatformGlobalDestination ? (
            <p className="platform-scope-badge">{t('platform.scopePlatformGlobal')}</p>
          ) : null}
          <div hidden={tab !== 'keys'}>
            <ProviderKeysTab
              key={tenantId}
              http={http}
              tenantId={tenantId}
              keys={keys}
              busy={busy}
              setBusy={setBusy}
              onToast={onToast}
              isActive={tab === 'keys'}
              onOpenAssignment={() => setTab('assignment')}
              onOpenManualProviderModels={() => {
                setDefinitionKind('provider');
                setTab('manualProviderModels');
              }}
              onRegistered={async () => {
                await loadTenants();
                await reload(tenantId.trim());
              }}
            />
          </div>
          <div hidden={tab !== 'geminiSearch'}>
            <GeminiSearchSettings
              key={tenantId}
              http={http}
              tenantId={tenantId}
              webSearchBackends={webSearchBackends}
              busy={busy}
              setBusy={setBusy}
              onToast={onToast}
              onChanged={() => reload(tenantId.trim())}
            />
          </div>
          <div hidden={tab !== 'imageGeneration' && tab !== 'videoGeneration'}>
            <MediaGenerationSettings
              key={tenantId}
              activeKind={tab === 'videoGeneration' ? 'video' : 'image'}
              http={http}
              tenantId={tenantId}
              keys={keys}
              mediaRoutes={mediaRoutes}
              tenant={tenants.find((tenant) => tenant.id === tenantId)}
              busy={busy}
              setBusy={setBusy}
              onToast={onToast}
              onChanged={async () => {
                await loadTenants();
                await reload(tenantId.trim());
              }}
            />
          </div>
          {tab === 'voice' ? <VoiceSettings http={http} tenantId={tenantId} /> : null}
          <div hidden={tab !== 'manualProviderModels'}>
            <div
              className="definition-kind-selector"
              role="group"
              aria-label={t('platform.definitionKindNavigation')}
            >
              <button
                type="button"
                className="secondary"
                aria-pressed={definitionKind === 'provider'}
                onClick={() => setDefinitionKind('provider')}
              >
                {t('platform.definitionKindProvider')}
              </button>
              <button
                type="button"
                className="secondary"
                aria-pressed={definitionKind === 'dedicated'}
                onClick={() => setDefinitionKind('dedicated')}
              >
                {t('platform.definitionKindDedicated')}
              </button>
            </div>
            <div hidden={definitionKind !== 'provider'}>
              <ManualProviderModelsPanel
                http={http}
                models={providerModels}
                setModels={setProviderModels}
                {...(selectedProviderModel ? { selected: selectedProviderModel } : {})}
                setSelected={setSelectedProviderModel}
                busy={busy}
                setBusy={setBusy}
                onToast={onToast}
                onOpenDeployment={(model) => {
                  setSelectedProviderModel(model);
                  setTab('modelDeployment');
                }}
                onChanged={async () => {
                  await loadTenants();
                  await reload(tenantId.trim());
                }}
              />
            </div>
            <div hidden={definitionKind !== 'dedicated'}>
              <CustomModelDefinitionsPanel
                http={http}
                onToast={onToast}
                onOpenDeployment={(partnerId) => {
                  setCustomModelPartnerId(partnerId);
                  setTab('modelDeployment');
                }}
              />
            </div>
          </div>
          <div hidden={tab !== 'modelDeployment'}>
            <ModelDeploymentPanel
              http={http}
              tenants={tenants}
              tenantId={tenantId}
              {...(selectedProviderModel ? { selected: selectedProviderModel } : {})}
              busy={busy}
              setBusy={setBusy}
              onToast={onToast}
              onDefinitionChanged={(model) => {
                setSelectedProviderModel(model);
                setProviderModels((current) =>
                  current.map((item) =>
                    item.provider === model.provider && item.modelKey === model.modelKey
                      ? model
                      : item
                  )
                );
              }}
              onOpenDefinitions={() => {
                setDefinitionKind('provider');
                setTab('manualProviderModels');
              }}
              onOpenAssignment={() => setTab('assignment')}
              onChanged={async () => {
                await loadTenants();
                await reload(tenantId.trim());
              }}
            />
            <CustomModelDeploymentSection
              http={http}
              tenants={tenants}
              partnerId={customModelPartnerId}
              setPartnerId={setCustomModelPartnerId}
              busy={busy}
              setBusy={setBusy}
              onToast={onToast}
              onOpenAssignment={() => setTab('assignment')}
              onChanged={async () => {
                await loadTenants();
                await reload(tenantId.trim());
              }}
            />
          </div>
          <div hidden={tab !== 'assignment'}>
            <ModelAssignmentTab
              key={tenantId}
              tenantId={tenantId}
              assignment={assignment}
              selection={assignmentSelection}
              setSelection={setAssignmentSelection}
              catalog={catalog}
              busy={busy || assignmentBusy}
              onSave={saveAssignment}
            />
          </div>
          <div hidden={tab !== 'autoRouting'}>
            <AutoRoutingTab key={tenantId} http={http} tenantId={tenantId} onToast={onToast} />
          </div>
          <div hidden={tab !== 'recommendedModels'}>
            <RecommendedModelsPanel key={tenantId} http={http} tenantId={tenantId} onToast={onToast} active={tab === 'recommendedModels'} onOpenSettings={setTab} />
          </div>
          {tab === 'loginDefault' && tenantHttp && <TenantLoginDefaultModelScreen key={tenantId} http={tenantHttp} tenantId={tenantId} onToast={onToast} />}
          {tab === 'modelAccess' && tenantHttp && <ModelsScreen key={tenantId} http={tenantHttp} platformHttp={http} tenantId={tenantId} isPlatformAdmin onToast={onToast} />}
          <div hidden={tab !== 'tools'}>
            <ToolActivationTab
              key={tenantId}
              tenantId={tenantId}
              assignable={assignableConnectors}
              selected={selectedConnectors}
              setSelected={setSelectedConnectors}
              busy={busy}
              onSave={saveConnectors}
            />
          </div>
          <div hidden={tab !== 'capabilitySources'}>
            <CapabilitySourcesTab
              http={http}
              onToast={onToast}
              isActive={tab === 'capabilitySources'}
              {...(activeTenant ? { tenant: activeTenant } : {})}
            />
          </div>
          <div hidden={tab !== 'socialLogin'}>
            <SocialLoginTab key={tenantId} http={http} tenantId={tenantId} onToast={onToast} />
          </div>
          <div hidden={tab !== 'selfSignup'}>
            <SignupTab
              key={tenantId}
              http={http}
              {...(activeTenant ? { tenant: activeTenant } : {})}
              tenants={tenants}
              onToast={onToast}
              isActive={tab === 'selfSignup'}
            />
          </div>
          <div hidden={tab !== 'organizationSso'}>
            <OrganizationSsoTab key={tenantId} http={http} tenantId={tenantId} onToast={onToast} />
          </div>
        </div>
      </div>
      {isModelSettingsTab(tab) ? (
        <details className="model-workflow-help">
          <summary>{t('platform.setupGuide')}</summary>
          <p>{t('platform.setupGuideBody')}</p>
          {tenantId ? <p>{t('platform.scopeTenantHint', onboardingProgress)}</p> : null}
        </details>
      ) : null}
    </section>
  );
}
